import {getStripeProducts} from './stripe';
import type {Organization} from '~/types/api';
import type Stripe from 'stripe';
export interface PlanDetails {
  quantity: number;
  isAnnual: boolean;
  priceId: string;
}

export interface PlanObject {
  free?: PlanDetails;
  reveal?: PlanDetails;
  revealAnnual?: PlanDetails;
  target?: PlanDetails;
  api?: PlanDetails;
  customIntentTopic?: PlanDetails;
  emailEnrichment?: PlanDetails;
  identifiedVisitors?: PlanDetails;
  adAudiences?: PlanDetails;
  bidAgents?: PlanDetails;
  coupon?: Stripe.Coupon;
  discountNumber?: number;
  discountPercentages?: number;
  planName?: string;
  isAnnualPlan?: boolean;
  hasPaidPlan?: boolean;
  [key: string]: PlanDetails | Stripe.Coupon | string | boolean | number | undefined;
}

interface ProductObj {
  product: string;
  quantity: number;
  isAnnual: boolean;
  priceId: string;
}

interface StripeProduct {
  productId: string;
  [key: string]: any;
}

interface StripeProducts {
  [key: string]: StripeProduct;
}

export const usePlan = async (): Promise<PlanObject> => {
  const org = useState<Organization>('org');

  // Guard against undefined org value (can happen on initial page load/refresh)
  if (!org?.value) {
    return {
      planName: '',
      isAnnualPlan: false,
      hasPaidPlan: false,
    };
  }

  let stripeProducts = {};
  if (getStripeProducts) {
    stripeProducts = getStripeProducts();
  }

  const currentProductObjs: ProductObj[] =
    org.value?.subscription?.items?.data?.map((item: Stripe.SubscriptionItem) => {
      return {
        product: item.price.product as string,
        quantity: item.quantity as number,
        isAnnual: item.price.recurring?.interval === 'year',
        priceId: item.price.id,
      };
    }) || [];

  const currentPlanObj: PlanObject = {};

  currentProductObjs.forEach((productObj: ProductObj) => {
    const product = findProduct(stripeProducts as StripeProducts, productObj.product);
    if ((productObj.quantity > 0 && product) || product === 'api') {
      currentPlanObj[product] = {
        quantity: productObj.quantity,
        isAnnual: productObj.isAnnual,
        priceId: productObj.priceId,
      };
    }
  });

  currentPlanObj.planName = calculatePlanName(currentPlanObj);
  currentPlanObj.isAnnualPlan = getIsAnnualPlan(currentPlanObj);
  currentPlanObj.hasPaidPlan = hasPaidPlan(currentPlanObj);

  if (org.value?.subscription?.discount) {
    const discount = org.value.subscription.discount;

    // Check if the discount is a coupon
    if (discount.coupon) {
      currentPlanObj.coupon = discount.coupon;
      currentPlanObj.discountNumber = (discount.coupon.amount_off || 0) / 100 || 0;
      currentPlanObj.discountPercentages = discount.coupon.percent_off || 0;
    }
  }

  return currentPlanObj;
};

const hasPaidPlan = (plan: PlanObject) => {
  return !!(plan?.reveal || plan?.revealAnnual || plan?.target);
};

const getIsAnnualPlan = (plan: PlanObject) => {
  return plan?.reveal?.isAnnual || plan?.revealAnnual?.isAnnual || plan?.target?.isAnnual || false;
};

const calculatePlanName = (plan: PlanObject) => {
  const planTypes = ['free', 'reveal', 'revealAnnual', 'target', 'api'] as const;
  return planTypes.find(type => plan?.[type]) || '';
};

const findProduct = (stripeProducts: StripeProducts, productId: string): string | undefined => {
  let product: string | undefined;
  Object.keys(stripeProducts).forEach((key: string) => {
    if (stripeProducts[key].productId === productId) {
      product = key;
    }
  });
  return product;
};
